import { ChevronDownIcon } from "@xola/icons";
import { Label, Modal, SubmitButton } from "@xola/ui-kit";
import { sortBy, uniqueId } from "lodash";
import React, { useState } from "react";
import { Except } from "type-fest";
import { ProductTermException, SelectionState } from "types";
import { useSearch } from "../../../hooks/useSearch";
import { useSelection } from "../../../hooks/useSelection";
import { useModal } from "../../../hooks/useVisibility";
import { defaultProductTermException } from "../../../models/ProductTermException";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";
import { FilterItem, SelectAllItem } from "../../Dashboard/Filters/ListFilter";
import { MultiSelectPopover } from "../../MultiSelectPopover";
import { productTermExceptionValidationSchema } from "./TermsTemplateFormValues";
import { TermsTemplatePurchaseRuleInput } from "./TermsTemplatePurchaseRuleInput";

interface EditProductTermExceptionModalProps {
    isOpen: boolean;
    title: string;
    productTermException: ProductTermException | null;
    experiences: Array<{ id: ID; name: string }>;
    packages: Array<{ id: ID; name: string }>;
    onSubmit: (productTermException: ProductTermException) => void;
    onClose: () => void;
}

export const EditProductTermExceptionModal = ({
    isOpen,
    title,
    productTermException,
    experiences,
    packages,
    onSubmit,
    onClose,
}: EditProductTermExceptionModalProps) => {
    return (
        <Modal size="medium" isOpen={isOpen} onClose={onClose} onSubmit={onSubmit}>
            <ModalContent
                title={title}
                productTermException={productTermException}
                experiences={experiences}
                packages={packages}
                onClose={onClose}
                onSubmit={onSubmit}
            />
        </Modal>
    );
};

const ModalContent = ({
    title,
    productTermException,
    experiences,
    packages,
    onClose,
    onSubmit,
}: Except<EditProductTermExceptionModalProps, "isOpen">) => {
    const popoverId = uniqueId("popoverParentId");

    return (
        <XolaFormik
            enableReinitialize
            validationSchema={productTermExceptionValidationSchema}
            initialValues={productTermException ?? defaultProductTermException}
            onSubmit={onSubmit}
        >
            {({ handleSubmit, values, setFieldValue, errors }) => {
                const handleProductSelectorConfirm = (value: SelectionState) => {
                    const selectedProducts = value.all
                        ? [...experiences, ...packages].map((product) => product.id)
                        : value.selected;
                    setFieldValue("products", sortBy(selectedProducts));
                };

                return (
                    <>
                        <Modal.Header>{title}</Modal.Header>

                        <Modal.Body>
                            <div>
                                <div id={popoverId} data-testid="product-term-exception-products">
                                    <Label>Products</Label>

                                    <ProductSelector
                                        popoverId={popoverId}
                                        experiences={experiences}
                                        packages={packages}
                                        selectedProducts={values.products}
                                        onApply={handleProductSelectorConfirm}
                                    />
                                    {errors.products && (
                                        <span className="mt-2 mb-4 text-sm font-bold text-danger">Select Products</span>
                                    )}
                                </div>

                                <div className="mb-2" data-testid="product-term-exception-discount">
                                    <Label>Discount</Label>

                                    <TermsTemplatePurchaseRuleInput
                                        autoFocus
                                        value={values.discount}
                                        field="discount"
                                        errors={errors?.discount}
                                        onChange={(value) => setFieldValue("discount", value)}
                                    />
                                </div>

                                <div data-testid="product-term-exception-commission">
                                    <Label>Commission</Label>

                                    <TermsTemplatePurchaseRuleInput
                                        value={values.commission}
                                        field="commission"
                                        errors={errors?.commission}
                                        onChange={(value) => setFieldValue("commission", value)}
                                    />
                                </div>
                            </div>
                        </Modal.Body>

                        <Modal.Footer>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                            <SubmitButton type="submit" onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

interface ProductSelectorProps {
    popoverId: string;
    experiences: Array<{ id: ID; name: string }>;
    packages: Array<{ id: ID; name: string }>;
    selectedProducts: ID[];
    onApply: (value: SelectionState) => void;
}

const ProductSelector = ({ popoverId, experiences, packages, selectedProducts, onApply }: ProductSelectorProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const selectProductModal = useModal();
    const filteredExperiences = useSearch(experiences, (experience) => [experience.name], searchTerm);
    const filteredPackages = useSearch(packages, (_package) => [_package.name], searchTerm);
    const isFiltered = searchTerm.length > 0;
    const isSubset = isFiltered;
    const selection = useSelection(
        [...filteredExperiences, ...filteredPackages],
        isSubset,
        {
            all: false,
            selected: selectedProducts,
        },
        false,
    );
    const [selectedProductsWithName, setSelectedProductsWithName] = useState(() => {
        if (selection.isSelectedAll) {
            return [...filteredExperiences, ...filteredPackages];
        }

        return [...filteredExperiences, ...filteredPackages].filter((product) =>
            selection.state.selected.includes(product.id),
        );
    });
    const [productsText, setProductsText] = useState(
        selection.isSelectedAll
            ? "All products selected"
            : selectedProductsWithName.map((product) => product.name).join(", "),
    );

    const handleSearch = (text: string): void => {
        setSearchTerm(text.trim());
    };

    const handleCheckboxChange = (product: { id: ID; name: string }) => {
        if (selection.isSelectedAll) {
            setSelectedProductsWithName(
                [...filteredExperiences, ...filteredPackages].filter((item) => item.id !== product.id),
            );
        } else if (selectedProductsWithName.some((item) => item.id === product.id)) {
            setSelectedProductsWithName([...selectedProductsWithName.filter((item) => item.id !== product.id)]);
        } else {
            setSelectedProductsWithName([...selectedProductsWithName, product]);
        }

        selection.select(product);
    };

    const handleApply = () => {
        onApply(selection.state);
        setProductsText(
            selection.isSelectedAll
                ? "All products selected"
                : selectedProductsWithName.map((product) => product.name).join(", "),
        );
        selectProductModal.hide();
        setSearchTerm("");
    };

    const handleSelectAll = () => {
        // if there are no available experiences and packages to select, do not toggle select all;
        if (experiences.length === 0 && packages.length === 0) {
            return;
        }

        if (selection.isSelectedAll) {
            setSelectedProductsWithName([]);
        }

        selection.selectAll();
    };

    return (
        <div className="space-y-2">
            <div className="space-y-2">
                <MultiSelectPopover
                    showSearch
                    placement="bottom-start"
                    popoverParentId={popoverId}
                    target={<SelectedProductList productsText={productsText} onClick={selectProductModal.toggle} />}
                    isVisible={selectProductModal.isVisible}
                    selectedCount={selection.count}
                    childClassName="max-h-60 no-translate"
                    onApply={handleApply}
                    onCancel={selectProductModal.hide}
                    onSearch={handleSearch}
                >
                    <SelectAllItem
                        isAllSelected={selection.isSelectedAll}
                        selectedCount={selection.count}
                        onChange={handleSelectAll}
                    />
                    {filteredExperiences.length > 0 && (
                        <>
                            <MultiSelectPopover.Header>
                                <span>Listings</span>
                            </MultiSelectPopover.Header>

                            {filteredExperiences.map((experience) => (
                                <FilterItem
                                    key={experience.id}
                                    label={experience.name}
                                    isChecked={selection.isSelected(experience)}
                                    className="h-12 border-b border-gray-light px-3 py-4 last:border-b-0"
                                    onChange={() => handleCheckboxChange(experience)}
                                />
                            ))}
                        </>
                    )}

                    {filteredPackages.length > 0 && (
                        <>
                            <MultiSelectPopover.Header>
                                <span>Packages</span>
                            </MultiSelectPopover.Header>

                            {filteredPackages.map((_package) => (
                                <FilterItem
                                    key={_package.id}
                                    label={_package.name}
                                    isChecked={selection.isSelected(_package)}
                                    className="h-12 border-b border-gray-light px-3 py-4 last:border-b-0"
                                    onChange={() => handleCheckboxChange(_package)}
                                />
                            ))}
                        </>
                    )}
                </MultiSelectPopover>
            </div>
        </div>
    );
};

interface SelectedProductListProps {
    productsText: string;
    onClick: () => void;
}

const SelectedProductList = ({ productsText, onClick }: SelectedProductListProps) => {
    return (
        <div
            className="mb-2 flex h-10 w-full items-center justify-between rounded border border-gray-light px-3"
            data-testid="product-term-exception-products-toggle"
            onClick={onClick}
        >
            <span className="float-left block w-80 overflow-hidden text-ellipsis whitespace-nowrap text-gray-darker">
                {productsText}
            </span>
            <ChevronDownIcon />
        </div>
    );
};
